/**
 * Draft of the agenda's « Lien de RDV » feature, and the rules that decide who
 * a link can name.
 *
 * The address itself is no longer composed here: a link is a booking code the
 * server mints, and the server owns the patient-web host. What is left is the
 * options the pharmacist assembles and the reading of them.
 */

export type BookingLinkMode = 'one' | 'multi' | 'any';
export type BookingLinkCombine = 'any' | 'all';

export interface BookingLinkLaboratory {
    id: number;
    name: string;
}

/** Options of the link being composed in the share modal. */
export interface BookingLinkDraft {
    mode: BookingLinkMode;
    /** Access point ids; one entry in `one` mode, empty in `any` mode. */
    operatorIds: number[];
    /** Only meaningful in `multi` mode. */
    combine: BookingLinkCombine;
    appointmentTypeId: number | null;
    laboratory: BookingLinkLaboratory | null;
    /** How long before a slot the patient may still take it. */
    minNoticeMinutes: number;
    /** How far ahead the patient may book. */
    horizonDays: number;
}

/**
 * A French mobile number reduced to its canonical `06…`/`07…` form, or null
 * when it is not one — a landline, or anything malformed.
 *
 * Comparing numbers needs this: the same mobile reaches us as « 06 12 34 56
 * 78 » from a patient file and as « +33612345678 » once typed, and a raw
 * string comparison would call them different.
 */
export const normalizeFrenchMobile = (phone: string): string | null => {
    const digits = phone.replace(/[\s.\-()]/g, '');
    const match = /^(?:0|\+33|0033)([67]\d{8})$/.exec(digits);

    return match ? `0${match[1]}` : null;
};

/**
 * A French mobile number (06/07, or the +33 form), whatever the grouping
 * characters. Landlines are refused: the link leaves by SMS.
 */
export const isValidFrenchMobile = (phone: string): boolean => normalizeFrenchMobile(phone) !== null;

export const DEFAULT_MIN_NOTICE_MINUTES = 120;
export const DEFAULT_HORIZON_DAYS = 60;

/**
 * The values a stepper walks through, coarse on purpose: the pharmacist sets a
 * booking window, not a stopwatch. A link carrying something else — set from
 * elsewhere, or a default that has since moved — is kept as it is and simply
 * steps onto the nearest rung.
 */
export const NOTICE_LADDER = [0, 30, 60, 120, 240, 720, 1440, 2880, 4320];

export const HORIZON_LADDER = [7, 14, 30, 60, 90, 180, 365];

/**
 * The short reading a stepper shows: minutes below the hour, hours below the
 * day, days beyond. What the delay *means* is carried by the label beside it,
 * so it is not repeated here.
 */
export const formatNotice = (minutes: number): string => {
    if (minutes <= 0) {
        return 'Dès maintenant';
    }

    if (minutes % 1440 === 0) {
        const days = minutes / 1440;

        return days === 1 ? '1 jour' : `${days} jours`;
    }

    return minutes % 60 === 0 ? `${minutes / 60} h` : `${minutes} min`;
};

export const formatHorizon = (days: number): string => {
    if (days % 365 === 0) {
        const years = days / 365;

        return years === 1 ? '1 an' : `${years} ans`;
    }

    if (days % 30 === 0) {
        return `${days / 30} mois`;
    }

    if (days % 7 === 0) {
        const weeks = days / 7;

        return weeks === 1 ? '1 semaine' : `${weeks} semaines`;
    }

    return days === 1 ? '1 jour' : `${days} jours`;
};

/**
 * The next value up or down the ladder.
 *
 * A value off the ladder snaps to its nearest rung before moving, so a link
 * saved with an odd window is never stuck: one press brings it back in line.
 * Returns the same value at either end, which is what disables the button.
 */
export const stepOnLadder = (ladder: number[], current: number, direction: 1 | -1): number => {
    const index = ladder.indexOf(current);

    if (index === -1) {
        const nearest = ladder.reduce((best, value) =>
            Math.abs(value - current) < Math.abs(best - current) ? value : best);

        return nearest;
    }

    return ladder[Math.min(Math.max(index + direction, 0), ladder.length - 1)];
};

export interface PresetLike {
    mode: BookingLinkMode;
    operator_ids: number[];
    combine: BookingLinkCombine | null;
    appointment_type_id: number | null;
    laboratory: BookingLinkLaboratory | null;
    min_notice_minutes: number;
    horizon_days: number;
}

/**
 * Rebuild a draft from a stored preset against the current team: members who
 * left are dropped — a habilitation never drops anyone — and a preset whose
 * whole selection vanished falls back to the pharmacy-wide link instead of
 * producing a link nobody can honour.
 *
 * The type is kept as stored, even one hidden or deleted since: the link is
 * then suspended, and dropping the type here would erase it for good on the
 * next save.
 */
export const draftFromPreset = (
    preset: PresetLike,
    teamOperatorIds: number[]
): BookingLinkDraft => {
    const appointmentTypeId = preset.appointment_type_id;

    const kept = preset.operator_ids.filter((id) => teamOperatorIds.includes(id));

    /*
     * Defaulted for the same reason the saved list defaults them: a preset
     * read from an API that predates the booking codes carries no window, and
     * the chips would then have no value to highlight.
     */
    const window = {
        minNoticeMinutes: preset.min_notice_minutes ?? DEFAULT_MIN_NOTICE_MINUTES,
        horizonDays: preset.horizon_days ?? DEFAULT_HORIZON_DAYS
    };

    if (preset.mode === 'any' || !kept.length) {
        return {
            mode: 'any',
            operatorIds: [],
            combine: preset.combine ?? 'any',
            appointmentTypeId,
            laboratory: preset.laboratory,
            ...window
        };
    }

    return {
        mode: preset.mode === 'multi' ? 'multi' : 'one',
        operatorIds: preset.mode === 'one' ? [kept[0]] : kept,
        combine: preset.combine ?? 'any',
        appointmentTypeId,
        laboratory: preset.laboratory,
        ...window
    };
};
